import { Box, Typography, useTheme } from "@mui/material";
import * as d3 from "d3";
import { useEffect, useRef } from "react";
import PropTypes from "prop-types";


const PieChart = ({ data = [], height = 300, colors, showLegend = false, legendTitle, onSelect, selectedKey = null }) => {
    const theme = useTheme();
    const containerRef = useRef(null);
    const svgRef = useRef(null);

    const sliceColor = (index) => colors?.[index % colors.length] || theme.palette.primary.main;

    const onSelectRef = useRef(onSelect);
    useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const draw = () => {
            const width = container.clientWidth;
            if (!width) return;

            const svg = d3.select(svgRef.current)
                .attr("width", width)
                .attr("height", height);
            svg.selectAll("*").remove();

            if (!data.length) return;

            const radius = Math.min(width, height) / 2 - 8;
            if (radius <= 0) return;

            const g = svg.append("g")
                .attr("transform", `translate(${width / 2},${height / 2})`);

            const pie = d3.pie().value(d => d.value).sort(null);
            const arc = d3.arc().innerRadius(0).outerRadius(radius);
            const total = d3.sum(data, d => d.value) || 0;

            const interactive = Boolean(onSelectRef.current);
            const slices = g.selectAll(".slice")
                .data(pie(data))
                .join("g")
                .attr("class", "slice")
                .attr("opacity", d => (selectedKey && d.data.key !== selectedKey ? 0.3 : 1))
                .style("cursor", interactive ? "pointer" : null)
                .on("click", interactive ? (event, d) => onSelectRef.current(d.data) : null);

            slices.append("path")
                .attr("d", arc)
                .attr("fill", (d, i) => sliceColor(i));

            slices.filter(d => total > 0 && (d.endAngle - d.startAngle) > 0.25)
                .append("text")
                .attr("transform", d => `translate(${arc.centroid(d)})`)
                .style("text-anchor", "middle")
                .style("font-size", "11px")
                .style("fill", theme.palette.primary.contrastText)
                .call(text => {
                    text.append("tspan")
                        .attr("x", 0)
                        .attr("dy", "-0.1em")
                        .text(d => d.data.label);
                    text.append("tspan")
                        .attr("x", 0)
                        .attr("dy", "1.2em")
                        .text(d => d.data.value);
                });
        };

        draw();

        let frame = null;
        let lastWidth = container.clientWidth;

        const resizeObserver = new ResizeObserver(() => {
            if (frame) return;
            frame = requestAnimationFrame(() => {
                frame = null;
                const width = container.clientWidth;
                if (width === lastWidth) return;
                lastWidth = width;
                draw();
            });
        });
        resizeObserver.observe(container);
        return () => {
            if (frame) cancelAnimationFrame(frame);
            resizeObserver.disconnect();
        };
    }, [data, height, colors, theme, selectedKey]);

    return (
        <div ref={containerRef} style={{ width: "100%" }}>
            {showLegend && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                    {legendTitle && (
                        <Typography variant="caption" fontWeight={600} color="text.secondary">{legendTitle}</Typography>
                    )}
                    {data.map((d, i) => (
                        <Box key={d.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Box sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: sliceColor(i)
                            }} />
                            <Typography variant="caption" color="text.secondary">{d.label}</Typography>
                        </Box>
                    ))}
                </Box>
            )}
            <svg ref={svgRef} />
        </div>
    );
};

PieChart.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        value: PropTypes.number,
    })),
    height: PropTypes.number,
    colors: PropTypes.arrayOf(PropTypes.string),
    showLegend: PropTypes.bool,
    legendTitle: PropTypes.string,
    onSelect: PropTypes.func,
    selectedKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default PieChart;
